import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, message, Card, Row, Col } from 'antd';
import axios from 'axios';

const SEND_OTP_URL = 'https://dev.ajocard.net/users/v1/sendBvnOtp';
const VALIDATE_OTP_URL = 'https://dev.ajocard.net/users/v1/validateBvnOtp';
const RESET_PASSWORD_URL = 'https://dev.ajocard.net/users/v1/auth/resetPassword';

const ForgotPasswordOtp: React.FC = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const navigate = useNavigate();

  // Step 1: Send OTP
  const onSendOtp = async (values: any) => {
    setLoading(true);
    try {
      await axios.post(SEND_OTP_URL, { phoneNumber: values.phoneNumber });
      setPhoneNumber(values.phoneNumber);
      message.success('OTP sent to your phone!');
      setStep(2);
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Validate OTP
  const onValidateOtp = async (values: any) => {
    setLoading(true);
    try {
      await axios.post(VALIDATE_OTP_URL, { phoneNumber, otp: values.otp });
      setOtp(values.otp);
      message.success('OTP validated! Please reset your password.');
      setStep(3);
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const onResetPassword = async (values: any) => {
    setLoading(true);
    try {
      await axios.post(RESET_PASSWORD_URL, {
        username: phoneNumber,
        password: values.password,
        temporary: false,
      });
      message.success('Password reset successful! Redirecting to sign in...');
      setTimeout(() => {
        navigate('/auth/login');
      }, 1200);
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100vw',
        background: "#181818 url('/assets/svg/ajoCard%20Intro%20background%20pattern.svg') center center / cover no-repeat",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Card
        style={{ maxWidth: 400, width: '100%', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.97)' }}
      >
        {step === 1 && (
          <Form layout="vertical" onFinish={onSendOtp}>
            <Form.Item
              name="phoneNumber"
              label="Phone Number (linked to your account)"
              rules={[{ required: true, message: 'Please enter the phone number linked to your account' }]}
            >
              <Input placeholder="e.g. +23481xxxxxxxx" size="large" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block size="large">
                Send OTP
              </Button>
            </Form.Item>
          </Form>
        )}
        {step === 2 && (
          <Form layout="vertical" onFinish={onValidateOtp}>
            <Form.Item
              name="otp"
              label={`Enter the OTP sent to ${phoneNumber}`}
              rules={[{ required: true, message: 'Please enter the OTP sent to your phone' }]}
            >
              <Input placeholder="Enter OTP" size="large" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block size="large">
                Validate OTP
              </Button>
            </Form.Item>
          </Form>
        )}
        {step === 3 && (
          <Form layout="vertical" onFinish={onResetPassword}>
            <Form.Item name="password" label="New Password" rules={[{ required: true, message: 'Please enter your new password' }]}>
              <Input.Password placeholder="New Password" size="large" />
            </Form.Item>
            <Form.Item
              name="confirm"
              label="Confirm Password"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject('Passwords do not match!');
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm Password" size="large" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block size="large">
                Reset Password
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default ForgotPasswordOtp;
